import React from 'react'
import PropTypes from 'prop-types'

class ServiceCheckV2 extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      showResults: false,
      esgTypeAvailability: {},
      allEsgTypes: [],
      loading: false,
      isValidCode: true,
    }

    this.handleSubmit = this.handleSubmit.bind(this)
  }

  async componentDidMount() {
    try {
      const response = await fetch('/wp-json/dhsv/v1/esg-types')
      const data = await response.json()

      if (Array.isArray(data)) {
        this.setState({ allEsgTypes: data.map(type => type.name) })
      }
    } catch (error) {
      console.error('Error fetching ESG types:', error)
    }
  }

  async fetchAllEsgData() {
    const transientKey = 'esg_data_cache' // Unique key for the transient
    const transientExpiration = 30 * 86400 // Transient cache expiration 30 days in seconds

    try {
      // Check if the transient exists
      const cachedData = await fetch(
        `/wp-json/dhsv/v1/get-transient?key=${transientKey}`
      )
      const cachedResponse = await cachedData.json()

      if (cachedResponse && cachedResponse.data) {
        return cachedResponse.data // Return cached data if available
      }

      // If no cached data, fetch from the API
      let allEsgData = []
      const perPage = 10000
      let currentPage = 1

      const initialResponse = await fetch(
        `/wp-json/dhsv/v1/service-check?esg=true&page=${currentPage}&per_page=${perPage}`
      )
      const initialData = await initialResponse.json()

      if (initialData.esg) {
        allEsgData = initialData.esg
      }

      const totalPages = initialData.esg_pagination.total_pages || 1

      if (totalPages > 1) {
        const fetchPromises = []
        for (let page = 2; page <= totalPages; page++) {
          fetchPromises.push(
            fetch(
              `/wp-json/dhsv/v1/service-check?esg=true&page=${page}&per_page=${perPage}`
            ).then(res => res.json())
          )
        }

        const results = await Promise.all(fetchPromises)
        results.forEach(data => {
          if (data.esg) {
            allEsgData = allEsgData.concat(data.esg)
          }
        })
      }

      // Save the fetched data in a transient
      await fetch(`/wp-json/dhsv/v1/set-transient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: transientKey,
          data: allEsgData,
          expiration: transientExpiration,
        }),
      })

      return allEsgData
    } catch (error) {
      console.error('Error fetching ESG data:', error)
      return []
    }
  }

  async checkRecipientZip(datasetName, recipientZip) {
    try {
      const allEsgData = await this.fetchAllEsgData()
      const typeResults = {}

      // Initialize all types as available (true)
      this.state.allEsgTypes.forEach(type => {
        typeResults[type] = true
      })

      if (allEsgData.length > 0) {
        // Preprocess data into a lookup map
        const lookupMap = new Map()
        allEsgData.forEach(dataset => {
          const key = `${dataset.name}-${dataset.zip}`
          lookupMap.set(key, dataset.type)
        })

        // Check for the specific datasetName and recipientZip
        const key = `${datasetName}-${recipientZip}`
        if (lookupMap.has(key)) {
          typeResults[lookupMap.get(key)] = false
        }

        // Custom check for Thermo 10
        const thermoKey = `ThermoMed-${recipientZip}`
        if (lookupMap.has(thermoKey)) {
          typeResults['ESG Thermo 10'] = false
        }
      }

      this.setState({ esgTypeAvailability: typeResults })
      //console.log('esgTypeAvailability:', typeResults)
      return typeResults
    } catch (error) {
      console.error('Error checking recipient zip:', error)
      return null
    }
  }

  async fetchServiceCheckData(senderZip) {
    try {
      const response = await fetch('/wp-json/dhsv/v1/service-check?lzs=true')
      const data = await response.json()

      if (data.lzs) {
        for (const dataset of data.lzs) {
          const [start, end] = dataset['zip-range'].split('-').map(Number)
          if (senderZip >= start && senderZip <= end) {
            return dataset.name
          }
        }
      }

      return null
    } catch (error) {
      console.error('Error fetching service-check data:', error)
      return null
    }
  }

  async handleSubmit(event) {
    event.preventDefault()

    this.setState({ showResults: false, loading: true })

    const senderZip = document.getElementById('sender-zip').value
    const recipientZip = document.getElementById('recipient-zip').value
    const { validCodes } = this.props.data

    // Check if both ZIP codes are valid
    if (validCodes.includes(senderZip) && validCodes.includes(recipientZip)) {
      this.setState({ isValidCode: true })

      const datasetName = await this.fetchServiceCheckData(Number(senderZip))
      //console.log(`datasetName: ${datasetName}`)

      if (datasetName) {
        const typeResults = await this.checkRecipientZip(
          datasetName,
          recipientZip
        )

        if (typeResults) {
          Object.entries(typeResults).forEach(([type, isAvailable]) => {
            if (isAvailable) {
              console.log(`Service available for type: ${type}`)
            } else {
              console.log(`Service not available for type: ${type}`)
            }
          })
        } else {
          console.log('No matching recipient zip found.')
        }
      } else {
        console.log('No matching dataset found for sender zip.')
      }
    } else {
      this.setState({ isValidCode: false })
    }

    this.setState({ showResults: true, loading: false })
    this.renderResults()
  }

  renderResults() {
    const { isValidCode, showResults } = this.state

    if (!isValidCode || !showResults) {
      return null
    }

    const { strings, resultContent } = this.props
    const { esgTypeAvailability } = this.state

    return (
      <div className="service-check-v2__results">
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>{strings.resultTimeLabel8}</th>
              <th>{strings.resultTimeLabel9}</th>
              <th>{strings.resultTimeLabel10}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Express</td>
              <td>
                {esgTypeAvailability['ESG 8/9'] ? (
                  <div className="service-check-v2__results--correct"></div>
                ) : (
                  <div className="service-check-v2__results--wrong"></div>
                )}
              </td>
              <td>
                {esgTypeAvailability['ESG 8/9'] ? (
                  <div className="service-check-v2__results--correct"></div>
                ) : (
                  <div className="service-check-v2__results--wrong"></div>
                )}
              </td>
              <td>
                {esgTypeAvailability['ESG 10'] ? (
                  <div className="service-check-v2__results--correct"></div>
                ) : (
                  <div className="service-check-v2__results--wrong"></div>
                )}
              </td>
            </tr>
            <tr>
              <td>Ambient (15-25 °C)</td>
              <td></td>
              <td></td>
              <td>
                {esgTypeAvailability['ESG Ambient 10'] ? (
                  <div className="service-check-v2__results--correct"></div>
                ) : (
                  <div className="service-check-v2__results--wrong"></div>
                )}
              </td>
            </tr>
            <tr>
              <td>Thermo (2-8 °C)</td>
              <td></td>
              <td></td>
              <td>
                {esgTypeAvailability['ESG Thermo 10'] ? (
                  <div className="service-check-v2__results--correct"></div>
                ) : (
                  <div className="service-check-v2__results--wrong"></div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          className="service-check-v2__results--content"
          dangerouslySetInnerHTML={{ __html: resultContent }}
        ></div>
      </div>
    )
  }

  render() {
    const { strings } = this.props
    const { loading, isValidCode } = this.state

    return (
      <form className="service-check-v2__form" onSubmit={this.handleSubmit}>
        <div className="input-group input-group-lg mb-3">
          <input
            id="sender-zip"
            type="text"
            pattern="[0-9]{5}"
            required={true}
            className="form-control"
            placeholder={strings.placeholderSenderZip}
          />
        </div>
        <div className="input-group input-group-lg mb-3">
          <input
            id="recipient-zip"
            type="text"
            pattern="[0-9]{5}"
            required={true}
            className="form-control"
            placeholder={strings.placeholderRecipientZip}
          />
        </div>
        {!isValidCode && (
          <div className="zip-error-message text-danger">
            {strings.errorValidZip}
          </div>
        )}
        <div className="input-group input-group-lg mb-3">
          <button
            className="btn btn-primary service-check-v2__form-submit"
            type="submit"
          >
            {strings.buttonText} <i className="btb bt-arrow-right" />
          </button>
        </div>
        {loading && (
          <div className="loading-icon">
            <div className="spinner"></div>
            {strings.loadingText}
          </div>
        )}
        {this.renderResults()}
      </form>
    )
  }
}

ServiceCheckV2.propTypes = {
  data: PropTypes.object,
  strings: PropTypes.object,
  resultContent: PropTypes.string,
}

export default ServiceCheckV2
